/**
 * The path trail, drawn as finixt-ui's Breadcrumb and built from links.
 *
 * finixt-ui's crumbs are spans with an onClick: not focusable, no href. The
 * trail is how an operator leaves a tenant from the header, so every crumb
 * but the last is a react-router Link, and the last names the current page.
 */
import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './finixt.js';

export interface Crumb {
  label: string;
  /** Absent on the last crumb, and on a grouping crumb with no page of its own. */
  to?: string | undefined;
  icon?: string | undefined;
}

export interface BreadcrumbProps {
  items: Crumb[];
}

const CRUMB_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 'var(--text-body-sm)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textDecoration: 'none',
} as const;

/** The trail. */
export function Breadcrumb({ items }: BreadcrumbProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}
    >
      {items.map((item, i) => {
        const last = i === items.length - 1;
        const glyph = item.icon ? (
          <Icon
            name={item.icon}
            size={16}
            color={last ? 'var(--text-soft)' : 'var(--text-faint)'}
          />
        ) : null;
        return (
          <Fragment key={`${item.label}-${i}`}>
            {i > 0 ? <Icon name="chevron_right" size={15} color="var(--grey-600)" /> : null}
            {last ? (
              <span
                aria-current="page"
                style={{
                  ...CRUMB_STYLE,
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-strong)',
                }}
              >
                {glyph}
                {item.label}
              </span>
            ) : item.to ? (
              <Link to={item.to} style={{ ...CRUMB_STYLE, color: 'var(--text-soft)' }}>
                {glyph}
                {item.label}
              </Link>
            ) : (
              <span style={{ ...CRUMB_STYLE, color: 'var(--text-soft)' }}>
                {glyph}
                {item.label}
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
